import React from 'react';
import { BadgeProps } from '@frontend/shadcn/components/ui/badge';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Tooltip, TooltipTrigger, TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import {
  TooltipHeader,
  TooltipTitle,
  TooltipBody,
  TooltipItem,
  TooltipItemKey,
  TooltipItemValue,
  TooltipContent,
} from '../../../components/tooltip';
import { DismissableBadge } from '@/applications/Social/components/filters/badges/dismissable-badge';
import { formatNumberDefault } from '@/applications/Social/utils/formatNumber';

interface MaxEngagementsData {
  id: string; // Identifier for the filter, e.g., "maxEngagements"
  names: string[]; // Expects a single string element representing the rate, e.g., ["5.0"]
}

interface CreatorDemographicsMaxEngagementsBadgeProps {
  data: MaxEngagementsData;
  onDismiss: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

type CombinedProps = BadgeProps & CreatorDemographicsMaxEngagementsBadgeProps;

const CreatorDemographicsMaxEngagementsBadge = React.forwardRef<HTMLDivElement, CombinedProps>(
  ({ data, onDismiss, ...props }, ref) => {
    // Basic validation for data presence and structure
    if (!data || !data.id || !data.names || data.names.length === 0 || !data.names[0]) {
      return null;
    }

    const engagementValueStr = data.names[0];
    // Attempt to parse the engagement rate string
    const parsedEngagements = parseFloat(engagementValueStr);

    // Check if parsing resulted in a valid number
    if (isNaN(parsedEngagements)) {
      // Optionally log a warning for debugging purposes
      // console.warn(`Invalid engagement rate string provided: ${engagementValueStr}`);
      return null; // Don't render the badge if the data is invalid
    }

    // Format the engagement count for display
    const displayValue = formatNumberDefault(parsedEngagements);

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div> {/* Wrapping div for TooltipTrigger constraint */}
              <DismissableBadge
                ref={ref}
                key={data.id}
                id={data.id}
                variant="secondary"
                className="mr-2 mb-2" // Standard spacing
                aria-label="Maximum engagement rate filter" // ARIA label for accessibility
                role="button" // Indicate interactivity
                data-dd-action-name="open-max-engagements-filters" // DataDog action tracking
                {...props} // Spread any additional props
              >
                {/* Display the filter label and value */}
                Max Engagement: <span className="ml-1">{displayValue}</span>
                {/* Dismiss button */}
                <button
                  type="button"
                  onClick={onDismiss} // Handler for removing the filter
                  aria-label="Remove maximum engagement rate filter" // ARIA label for the button
                  className="flex ml-1 p-1 focus:outline-none focus:ring" // Styling for the button
                  data-dd-action-name="remove-max-engagements-filters" // DataDog action tracking
                >
                  <Cross2Icon className="h-3 w-3" /> {/* Dismiss icon */}
                </button>
                {/* Screen reader only text providing context for the dismiss action */}
                <span className="sr-only">
                  Remove maximum engagement rate filter for {displayValue}
                </span>
              </DismissableBadge>
            </div>
          </TooltipTrigger>
          {/* Tooltip content shown on hover/focus */}
          <TooltipContent>
            <TooltipHeader>
              <TooltipTitle>Maximum Engagement Rate Filter</TooltipTitle>
            </TooltipHeader>
            <TooltipBody>
              <TooltipItem>
                <TooltipItemKey>Maximum Engagement</TooltipItemKey>
                <TooltipItemValue>
                  {displayValue} {/* Display the formatted value */}
                </TooltipItemValue>
              </TooltipItem>
            </TooltipBody>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
);

// Set display name for React DevTools
CreatorDemographicsMaxEngagementsBadge.displayName = 'CreatorDemographicsMaxEngagementsBadge';

export default CreatorDemographicsMaxEngagementsBadge;
